import type { NearbyPlace, SocialContext } from "./types";
import { apiBase } from "./api";
import { filterPlaces, type SettingPref } from "./plans";
import {
  buildOverpassQuery,
  KIND_FALLBACK_NAME,
  normalizePlaceKind,
  SEARCH_FALLBACK_QUERIES,
} from "./placeCatalog";

const OVERPASS_ENDPOINTS = [
  "https://overpass-api.de/api/interpreter",
  "https://overpass.kumi.systems/api/interpreter",
  "https://overpass.private.coffee/api/interpreter",
];

function requestHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const headers: Record<string, string> = { Accept: "application/json", ...extra };
  // Custom User-Agent in the browser triggers a CORS preflight that Overpass/Photon reject.
  if (typeof document === "undefined") {
    headers["User-Agent"] = "Hermes/1.0 (https://rycso.es/hermes)";
  }
  return headers;
}

const PHOTON_QUERIES = SEARCH_FALLBACK_QUERIES;

export function placeDistanceMeters(
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
) {
  return haversine(a, b);
}

function haversine(a: { latitude: number; longitude: number }, b: { latitude: number; longitude: number }) {
  const R = 6371000;
  const dLat = ((b.latitude - a.latitude) * Math.PI) / 180;
  const dLon = ((b.longitude - a.longitude) * Math.PI) / 180;
  const lat1 = (a.latitude * Math.PI) / 180;
  const lat2 = (b.latitude * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

export { normalizePlaceKind } from "./placeCatalog";

/** Rating OSM 0–5 si existe; si no, undefined (el motor asigna 2.5). */
export function ratingFromTags(tags: Record<string, string>): number | undefined {
  const raw = tags.stars || tags.rating || tags["rating:stars"];
  if (!raw) return undefined;
  const n = Number(String(raw).replace(",", "."));
  if (!Number.isFinite(n)) return undefined;
  if (n > 5 && n <= 10) return Math.max(0, Math.min(5, n / 2));
  return Math.max(0, Math.min(5, n));
}

function paidFromTags(tags: Record<string, string>, kind: string): boolean | undefined {
  if (tags.fee === "no" || tags.fee === "free") return false;
  if (tags.fee === "yes") return true;
  if (
    kind === "kart" ||
    kind === "ski" ||
    kind === "bungee" ||
    kind === "bowling" ||
    kind === "parachuting" ||
    kind === "escape_game" ||
    kind === "theme_park" ||
    kind === "water_park" ||
    kind === "cinema" ||
    kind === "zoo" ||
    kind === "aquarium"
  ) {
    return true;
  }
  return undefined;
}

function placeName(tags: Record<string, string>, kind: string): string | null {
  const name = tags.name || tags["name:es"] || tags.brand || tags.operator || tags.ref;
  if (name) return name;
  return KIND_FALLBACK_NAME[kind] || null;
}

function withDistance(
  origin: { latitude: number; longitude: number },
  place: NearbyPlace,
  radius: number,
): NearbyPlace | null {
  const distanceMeters = Math.round(haversine(origin, place));
  if (distanceMeters > radius * 1.35) return null;
  return { ...place, distanceMeters };
}

function mergePlaces(list: NearbyPlace[]): NearbyPlace[] {
  const uniq = new Map<string, NearbyPlace>();
  for (const place of list) {
    const key = `${place.name.toLowerCase()}|${place.latitude.toFixed(4)}|${place.longitude.toFixed(4)}`;
    const prev = uniq.get(key);
    if (!prev || (place.distanceMeters || 999999) < (prev.distanceMeters || 999999)) {
      uniq.set(key, place);
    }
  }
  return [...uniq.values()].sort((a, b) => (a.distanceMeters || 0) - (b.distanceMeters || 0));
}

async function fetchWithTimeout(url: string, init: RequestInit, timeoutMs = 14000): Promise<Response> {
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    return await fetch(url, { ...init, signal: controller?.signal });
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function fetchOverpass(origin: {
  latitude: number;
  longitude: number;
  radius: number;
}): Promise<NearbyPlace[]> {
  const around = `(around:${origin.radius},${origin.latitude},${origin.longitude})`;
  const body = buildOverpassQuery(around, 700);
  for (const endpoint of OVERPASS_ENDPOINTS) {
    try {
      const response = await fetchWithTimeout(
        endpoint,
        {
          method: "POST",
          headers: requestHeaders({
            "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
          }),
          body: `data=${encodeURIComponent(body)}`,
        },
        22000,
      );
      if (!response.ok) continue;
      const data = (await response.json()) as {
        elements?: Array<{
          id: number;
          lat?: number;
          lon?: number;
          center?: { lat: number; lon: number };
          tags?: Record<string, string>;
        }>;
      };
      const places = (data.elements || [])
        .map((el) => {
          const latitude = el.lat ?? el.center?.lat;
          const longitude = el.lon ?? el.center?.lon;
          if (latitude == null || longitude == null) return null;
          const tags = el.tags || {};
          const kind = normalizePlaceKind(tags);
          const name = placeName(tags, kind);
          if (!name) return null;
          const rating = ratingFromTags(tags);
          const paid = paidFromTags(tags, kind);
          const outdoorSeating =
            tags.outdoor_seating === "yes" || tags.amenity === "biergarten" || tags.leisure === "outdoor_seating";
          return withDistance(
            origin,
            {
              id: `osm-${el.id}`,
              name,
              kind,
              address: [tags["addr:street"], tags["addr:housenumber"], tags["addr:city"]]
                .filter(Boolean)
                .join(" "),
              latitude,
              longitude,
              rating,
              paid,
              outdoorSeating,
              tags: Object.keys(tags).slice(0, 12),
            },
            origin.radius,
          );
        })
        .filter((item): item is NearbyPlace => Boolean(item));
      if (places.length) return places;
    } catch {
      /* try next mirror */
    }
  }
  return [];
}

/** Proxy Hostinger: más fiable que Overpass directo desde el navegador. */
async function fetchViaHermesApi(origin: {
  latitude: number;
  longitude: number;
  radius: number;
}): Promise<NearbyPlace[]> {
  try {
    const url = `${apiBase()}/places/nearby?lat=${origin.latitude}&lon=${origin.longitude}&radius=${origin.radius}`;
    const response = await fetchWithTimeout(url, { headers: requestHeaders() }, 35000);
    if (!response.ok) return [];
    const data = (await response.json()) as { places?: NearbyPlace[] };
    if (!Array.isArray(data.places)) return [];
    return data.places
      .map((place) =>
        withDistance(
          origin,
          {
            id: place.id,
            name: place.name,
            kind: place.kind || "lugar",
            address: place.address || "",
            latitude: place.latitude,
            longitude: place.longitude,
            rating: place.rating,
            paid: place.paid,
            isEvent: place.isEvent,
            tags: place.tags,
            outdoorSeating: place.outdoorSeating,
          },
          origin.radius,
        ),
      )
      .filter((item): item is NearbyPlace => Boolean(item));
  } catch {
    return [];
  }
}

async function fetchPhoton(origin: {
  latitude: number;
  longitude: number;
  radius: number;
}): Promise<NearbyPlace[]> {
  const results = await Promise.all(
    PHOTON_QUERIES.map(async ({ q, kind }) => {
      try {
        const deg = Math.max(0.008, origin.radius / 111320);
        const bbox = `${origin.longitude - deg},${origin.latitude - deg},${origin.longitude + deg},${origin.latitude + deg}`;
        const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&lat=${origin.latitude}&lon=${origin.longitude}&limit=10&lang=es&bbox=${bbox}`;
        const response = await fetchWithTimeout(
          url,
          { headers: requestHeaders() },
          9000,
        );
        if (!response.ok) return [];
        const data = (await response.json()) as {
          features?: Array<{
            geometry?: { coordinates?: number[] };
            properties?: { osm_id?: number; name?: string; street?: string; city?: string; osm_key?: string };
          }>;
        };
        return (data.features || [])
          .map((feature, index) => {
            const coords = feature.geometry?.coordinates;
            if (!coords || coords.length < 2) return null;
            const longitude = coords[0];
            const latitude = coords[1];
            const name = feature.properties?.name;
            if (!name) return null;
            return withDistance(origin, {
              id: `photon-${kind}-${feature.properties?.osm_id || index}`,
              name,
              kind,
              address: [feature.properties?.street, feature.properties?.city].filter(Boolean).join(" "),
              latitude,
              longitude,
            }, origin.radius);
          })
          .filter((item): item is NearbyPlace => Boolean(item));
      } catch {
        return [];
      }
    }),
  );
  return results.flat();
}

function geoapifyKey(): string {
  if (typeof process === "undefined" || !process.env) return "";
  return process.env.EXPO_PUBLIC_GEOAPIFY_API_KEY || process.env.VITE_GEOAPIFY_API_KEY || "";
}

async function fetchGeoapify(origin: {
  latitude: number;
  longitude: number;
  radius: number;
}): Promise<NearbyPlace[]> {
  const key = geoapifyKey();
  if (!key) return [];
  try {
    const cats =
      "catering.cafe,catering.restaurant,catering.bar,entertainment.museum,entertainment.cinema,entertainment.nightclub,leisure.park,commercial.supermarket,commercial.shopping_mall,education.library,tourism.attraction,tourism.sights,building.historic,activity.sport_club,entertainment.bowling";
    const url = `https://api.geoapify.com/v2/places?categories=${cats}&filter=circle:${origin.longitude},${origin.latitude},${origin.radius}&bias=proximity:${origin.longitude},${origin.latitude}&limit=80&apiKey=${encodeURIComponent(key)}`;
    const response = await fetchWithTimeout(url, { headers: requestHeaders() }, 10000);
    if (!response.ok) return [];
    const data = (await response.json()) as {
      features?: Array<{
        properties?: {
          place_id?: string;
          name?: string;
          formatted?: string;
          categories?: string[];
        };
        geometry?: { coordinates?: number[] };
      }>;
    };
    return (data.features || [])
      .map((feature, index) => {
        const coords = feature.geometry?.coordinates;
        if (!coords || coords.length < 2) return null;
        const categories = feature.properties?.categories || [];
        const blob = categories.join(" ");
        let kind = "lugar";
        if (/cafe/.test(blob)) kind = "cafe";
        else if (/restaurant/.test(blob)) kind = "restaurant";
        else if (/bar|pub/.test(blob)) kind = "bar";
        else if (/museum/.test(blob)) kind = "museum";
        else if (/park/.test(blob)) kind = "park";
        else if (/library/.test(blob)) kind = "library";
        else if (/supermarket/.test(blob)) kind = "supermarket";
        else if (/cinema/.test(blob)) kind = "cinema";
        else if (/nightclub|adult/.test(blob)) kind = "nightclub";
        else if (/bowling/.test(blob)) kind = "bowling";
        else if (/church|religion/.test(blob)) kind = "church";
        else if (/castle/.test(blob)) kind = "castle";
        else if (/bridge/.test(blob)) kind = "bridge";
        else if (/mountain|peak/.test(blob)) kind = "mountain";
        else if (/attraction|sights|monument/.test(blob)) kind = "monument";
        const name = feature.properties?.name;
        if (!name) return null;
        return withDistance(origin, {
          id: `geo-${feature.properties?.place_id || index}`,
          name,
          kind,
          address: feature.properties?.formatted || "",
          latitude: coords[1],
          longitude: coords[0],
        }, origin.radius);
      })
      .filter((item): item is NearbyPlace => Boolean(item));
  } catch {
    return [];
  }
}

async function fetchNominatim(origin: {
  latitude: number;
  longitude: number;
  radius: number;
}): Promise<NearbyPlace[]> {
  const deg = Math.max(0.01, origin.radius / 111320);
  const viewbox = `${origin.longitude - deg},${origin.latitude + deg},${origin.longitude + deg},${origin.latitude - deg}`;
  const queries = SEARCH_FALLBACK_QUERIES.slice(0, 28);
  const results = await Promise.all(
    queries.map(async ({ q, kind }) => {
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(q)}&lat=${origin.latitude}&lon=${origin.longitude}&limit=8&bounded=1&viewbox=${encodeURIComponent(viewbox)}`;
        const response = await fetchWithTimeout(url, { headers: requestHeaders() }, 8000);
        if (!response.ok) return [];
        const data = (await response.json()) as Array<{
          lat?: string;
          lon?: string;
          display_name?: string;
          name?: string;
          osm_id?: number;
        }>;
        return (data || [])
          .map((item, index) => {
            const latitude = Number(item.lat);
            const longitude = Number(item.lon);
            if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
            const name = item.name || item.display_name?.split(",")[0];
            if (!name) return null;
            return withDistance(
              origin,
              {
                id: `nom-${kind}-${item.osm_id || index}`,
                name,
                kind,
                address: item.display_name || "",
                latitude,
                longitude,
              },
              origin.radius,
            );
          })
          .filter((item): item is NearbyPlace => Boolean(item));
      } catch {
        return [];
      }
    }),
  );
  return results.flat();
}

export async function fetchNearbyPlaces(origin: {
  latitude: number;
  longitude: number;
  radius?: number;
}): Promise<NearbyPlace[]> {
  const radius = origin.radius ?? 5000;
  const point = { latitude: origin.latitude, longitude: origin.longitude, radius };
  const settled = await Promise.allSettled([
    fetchViaHermesApi(point),
    fetchOverpass(point),
    fetchPhoton(point),
    fetchNominatim(point),
    fetchGeoapify(point),
  ]);
  const lists = settled.map((item) => (item.status === "fulfilled" ? item.value : []));
  return mergePlaces(lists.flat()).slice(0, 500);
}

/** Prueba radios crecientes hasta reunir suficientes POIs. */
export async function fetchNearbyPlacesCascade(origin: {
  latitude: number;
  longitude: number;
  radii: number[];
}): Promise<{ places: NearbyPlace[]; radiusUsed: number }> {
  const radii = origin.radii.length ? origin.radii : [5000];
  let best: NearbyPlace[] = [];
  let radiusUsed = radii[0];
  for (const radius of radii) {
    try {
      const places = await fetchNearbyPlaces({
        latitude: origin.latitude,
        longitude: origin.longitude,
        radius,
      });
      if (places.length > best.length) {
        best = places;
        radiusUsed = radius;
      }
      const enough = radius <= 2500 ? 40 : 80;
      if (places.length >= enough) {
        return { places, radiusUsed: radius };
      }
    } catch {
      /* next radius */
    }
  }
  return { places: best, radiusUsed };
}

export async function fetchWeather(lat: number, lon: number) {
  const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,precipitation,weather_code`;
  try {
    const response = await fetchWithTimeout(url, { headers: requestHeaders() }, 8000);
    if (!response.ok) return null;
    return response.json() as Promise<{ current?: { temperature_2m?: number; precipitation?: number } }>;
  } catch {
    return null;
  }
}

export function openStreetMapEmbed(lat: number, lon: number, zoomPad = 0.012): string {
  return `https://www.openstreetmap.org/export/embed.html?bbox=${lon - zoomPad},${lat - zoomPad},${lon + zoomPad},${lat + zoomPad}&layer=mapnik&marker=${lat},${lon}`;
}

export function openStreetMapRouteEmbed(
  origin: { latitude: number; longitude: number },
  dest: { latitude: number; longitude: number },
): string {
  const minLat = Math.min(origin.latitude, dest.latitude);
  const maxLat = Math.max(origin.latitude, dest.latitude);
  const minLon = Math.min(origin.longitude, dest.longitude);
  const maxLon = Math.max(origin.longitude, dest.longitude);
  const padLat = Math.max(0.008, (maxLat - minLat) * 0.35);
  const padLon = Math.max(0.008, (maxLon - minLon) * 0.35);
  return `https://www.openstreetmap.org/export/embed.html?bbox=${minLon - padLon},${minLat - padLat},${maxLon + padLon},${maxLat + padLat}&layer=mapnik&marker=${dest.latitude},${dest.longitude}`;
}

export type MapPoint = { latitude: number; longitude: number; name?: string };

/** Literal JS para `bindPopup`: Leaflet lo pinta como HTML y los nombres vienen de OSM (texto libre). */
function popupLiteral(text: string): string {
  const html = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  return JSON.stringify(html).replace(/\u2028/g, "\\u2028").replace(/\u2029/g, "\\u2029");
}

/** Coordenada como número finito: se escribe tal cual dentro del <script> del mapa. */
function coord(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Copia del punto con latitud/longitud numéricas (un plan guardado o un servidor podría traer texto). */
function safePoint(point: MapPoint): MapPoint {
  return { latitude: coord(point.latitude), longitude: coord(point.longitude), name: typeof point.name === "string" ? point.name : undefined };
}

/** Full Leaflet page for WebView / iframe srcDoc. Never uses Google Maps Embed. */
export function leafletMapHtml(
  rawOrigin: MapPoint,
  rawDest?: MapPoint | null,
  rawWaypoints: MapPoint[] = [],
  rawNearby: MapPoint[] = [],
  options: { line?: MapPoint[]; originLabel?: string } = {},
): string {
  const origin = safePoint(rawOrigin);
  const dest = rawDest ? safePoint(rawDest) : null;
  const waypoints = (Array.isArray(rawWaypoints) ? rawWaypoints : []).filter(Boolean).map(safePoint);
  const nearby = (Array.isArray(rawNearby) ? rawNearby : []).filter(Boolean).map(safePoint);
  const mid = waypoints;
  const last = dest || (mid.length ? origin : null);
  const osm = last
    ? openStreetMapRouteEmbed(origin, last)
    : openStreetMapEmbed(origin.latitude, origin.longitude);
  const pts: MapPoint[] = [origin, ...mid];
  if (last && (last.latitude !== origin.latitude || last.longitude !== origin.longitude || mid.length)) {
    pts.push(last);
  }
  const markersJs = pts
    .map((point, index) => {
      const label = index === 0 ? options.originLabel || "Estás aquí" : point.name || `Parada ${index}`;
      return `L.marker([${point.latitude},${point.longitude}]).addTo(map).bindPopup(${popupLiteral(label)});`;
    })
    .join("");
  const nearbyJs = nearby
    .slice(0, 40)
    .map((point) => {
      const label = point.name || "Sitio cercano";
      return `L.circleMarker([${point.latitude},${point.longitude}],{radius:7,color:'#8fd4a8',weight:2,fillColor:'#8fd4a8',fillOpacity:0.85}).addTo(map).bindPopup(${popupLiteral(label)});`;
    })
    .join("");
  const linePts = pts.map((point) => `[${point.latitude},${point.longitude}]`).join(",");
  // Trazado real (por carretera) si se da: se aligera a ~600 puntos para no cargar el mapa.
  const rawLine = (Array.isArray(options.line) ? options.line : []).filter(Boolean).map(safePoint);
  const stride = Math.max(1, Math.ceil(rawLine.length / 600));
  const drawn = rawLine.filter((_, i) => i % stride === 0 || i === rawLine.length - 1);
  const drawnPts = drawn.map((point) => `[${point.latitude},${point.longitude}]`).join(",");
  const lineJs =
    drawn.length > 1
      ? `L.polyline([${drawnPts}],{color:'#e07a3d',weight:5}).addTo(map);map.fitBounds([${drawnPts}],{padding:[32,32]});`
      : pts.length > 1
      ? `L.polyline([${linePts}],{color:'#e07a3d',weight:5}).addTo(map);map.fitBounds([${linePts}],{padding:[32,32]});`
      : nearby.length
        ? `map.setView([${origin.latitude},${origin.longitude}],15);`
        : "";
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1"><link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/><style>html,body,#map,iframe{margin:0;height:100%;width:100%;border:0;background:#1b2430}</style></head><body><div id="map"></div><iframe id="osmfallback" title="Mapa" src="${osm.replace(/"/g, "&quot;")}" style="display:none"></iframe><script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script><script>try{var origin=[${origin.latitude},${origin.longitude}];var map=L.map('map').setView(origin,15);L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png',{maxZoom:19,attribution:'© OpenStreetMap'}).addTo(map);${markersJs}${nearbyJs}${lineJs}}catch(e){}setTimeout(function(){if(!window.L||!document.querySelector('.leaflet-pane')){document.getElementById('map').style.display='none';document.getElementById('osmfallback').style.display='block';}},2500);</script></body></html>`;
}

/** Mapa con un pin arrastrable. Avisa con postMessage `{ type: "hermes-pin", latitude, longitude }`. */
export function leafletPinPickerHtml(latitude: number, longitude: number): string {
  const lat = typeof latitude === "number" && Number.isFinite(latitude) ? latitude : 40.4168;
  const lon = typeof longitude === "number" && Number.isFinite(longitude) ? longitude : -3.7038;
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1"><link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/><style>html,body,#map{margin:0;height:100%;width:100%;background:#1b2430}</style></head><body><div id="map"></div><script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script><script>var map=L.map('map').setView([${lat},${lon}],14);L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png',{maxZoom:19,attribution:'© OpenStreetMap'}).addTo(map);var marker=L.marker([${lat},${lon}],{draggable:true}).addTo(map);function send(){var p=marker.getLatLng();var payload=JSON.stringify({type:'hermes-pin',latitude:p.lat,longitude:p.lng});if(window.ReactNativeWebView)window.ReactNativeWebView.postMessage(payload);else if(window.parent)window.parent.postMessage(payload,'*');}marker.on('dragend',send);map.on('click',function(e){marker.setLatLng(e.latlng);send();});</script></body></html>`;
}

/** Kept for old call sites: never load Google Embed as the top-level document. */
export function mapsIframeHtml(src: string): string {
  if (src.includes("<html")) return src;
  const safe = src.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  if (!/^https:\/\//i.test(src.trim()) || /google\.com\/maps\/embed/i.test(src)) {
    return leafletMapHtml({ latitude: 40.4168, longitude: -3.7038 });
  }
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1"><style>html,body,iframe{margin:0;padding:0;height:100%;width:100%;border:0;background:#1b2430}</style></head><body><iframe src="${safe}" allowfullscreen loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe></body></html>`;
}

export function googleMapsEmbed(lat: number, lon: number): string {
  return openStreetMapEmbed(lat, lon);
}

export function googleMapsLink(lat: number, lon: number, label?: string): string {
  // Con coordenadas válidas se abre el punto exacto: buscar por el título («Café en X (a 300 m)») lleva a otro sitio.
  const hasPoint = Number.isFinite(lat) && Number.isFinite(lon);
  const q = hasPoint ? `${lat},${lon}` : encodeURIComponent(label || "");
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
}

export type TravelMode = "walk" | "run" | "bike" | "car";

export function travelModeToGoogle(mode: TravelMode): string {
  return mode === "car" ? "driving" : mode === "bike" ? "bicycling" : "walking";
}

export function googleMapsDirections(
  origin: { latitude: number; longitude: number },
  dest: { latitude: number; longitude: number; name?: string },
  mode: TravelMode = "walk",
): string {
  const o = `${origin.latitude},${origin.longitude}`;
  const d = `${dest.latitude},${dest.longitude}`;
  return `https://www.google.com/maps/dir/?api=1&origin=${o}&destination=${d}&travelmode=${travelModeToGoogle(mode)}`;
}

export function googleMapsLoop(
  origin: { latitude: number; longitude: number },
  stops: Array<{ latitude: number; longitude: number }>,
  mode: TravelMode = "walk",
): string {
  const o = `${origin.latitude},${origin.longitude}`;
  const mids = stops
    .slice(0, 9)
    .map((stop) => `${stop.latitude},${stop.longitude}`)
    .join("|");
  const wp = mids ? `&waypoints=${encodeURIComponent(mids)}` : "";
  return `https://www.google.com/maps/dir/?api=1&origin=${o}&destination=${o}${wp}&travelmode=${travelModeToGoogle(mode)}`;
}

export function planMapsUrl(
  origin: { latitude: number; longitude: number } | null,
  plan: { map?: { lat: number; lon: number }; title: string },
  mode: TravelMode = "walk",
): string | null {
  if (!plan.map) return null;
  if (origin) {
    return googleMapsDirections(
      origin,
      { latitude: plan.map.lat, longitude: plan.map.lon, name: plan.title },
      mode,
    );
  }
  return googleMapsLink(plan.map.lat, plan.map.lon, plan.title);
}

export function googleMapsDirectionsEmbed(
  origin: { latitude: number; longitude: number },
  dest: { latitude: number; longitude: number },
  _mode: TravelMode = "walk",
): string {
  return openStreetMapRouteEmbed(origin, dest);
}

/** OSRM public router — one-way duration in minutes. */
export async function estimateRouteMinutes(
  origin: { latitude: number; longitude: number },
  dest: { latitude: number; longitude: number },
  mode: TravelMode,
): Promise<number | null> {
  const profile = mode === "car" ? "driving" : mode === "bike" ? "cycling" : "walking";
  const url = `https://router.project-osrm.org/route/v1/${profile}/${origin.longitude},${origin.latitude};${dest.longitude},${dest.latitude}?overview=false`;
  try {
    const response = await fetchWithTimeout(url, { headers: { Accept: "application/json" } }, 8000);
    if (!response.ok) return null;
    const data = (await response.json()) as {
      routes?: Array<{ duration?: number; distance?: number }>;
    };
    const seconds = data.routes?.[0]?.duration;
    if (seconds == null) return null;
    const factor = mode === "run" ? 0.55 : 1;
    return Math.round((seconds * factor) / 60);
  } catch {
    return null;
  }
}

export const ROUTE_INTEREST = new Set([
  "monument",
  "park",
  "viewpoint",
  "museum",
  "plaza",
  "square",
  "gallery",
  "library",
  "church",
  "castle",
  "attraction",
  "artwork",
  "ruins",
  "townhall",
  "bar",
  "restaurant",
  "cinema",
  "bowling",
  "cafe",
  "shop",
  "nightclub",
  "marketplace",
  "mountain",
  "bridge",
  "supermarket",
]);

const SPEED_M_PER_MIN: Record<TravelMode, number> = {
  walk: 75,
  run: 158,
  bike: 233,
  car: 533,
};

export function stayLocalRadiusMeters(mode: TravelMode): number {
  if (mode === "walk" || mode === "run") return 1400;
  if (mode === "bike") return 2200;
  return 3800;
}

export function interestScore(place: NearbyPlace): number {
  const scores: Record<string, number> = {
    monument: 12,
    castle: 12,
    ruins: 11,
    museum: 11,
    viewpoint: 10,
    mountain: 10,
    attraction: 9,
    park: 9,
    restaurant: 9,
    cinema: 9,
    plaza: 8,
    square: 8,
    gallery: 8,
    bowling: 8,
    bar: 8,
    marketplace: 8,
    church: 7,
    artwork: 7,
    cafe: 7,
    nightclub: 7,
    library: 7,
    bridge: 7,
    shop: 6,
    theatre: 6,
    townhall: 6,
    supermarket: 5,
    bakery: 5,
    books: 5,
    // Más tipos de sitio: con el filtro por categorías (ocio, compras, comida…) también deben poder ser parada.
    theme_park: 9, zoo: 9, aquarium: 9, water_park: 8, escape_game: 8, kart: 7, casino: 7, amusement_arcade: 6, miniature_golf: 6,
    circus: 6, planetarium: 8, observatory: 8, music_venue: 7, arts_centre: 7, mall: 7, department_store: 6, clothes: 6,
    furniture: 5, hardware: 5, doityourself: 5, variety: 5, gift: 5, electronics: 5, fast_food: 7, mcdonalds: 7, burgerking: 7,
    ice_cream: 6, biergarten: 7, sport: 6, stadium: 6, swimming: 6, fitness: 5, climbing: 6, historic: 9, memorial: 7,
    archaeological: 10, monastery: 9, fort: 9, tower: 8, lighthouse: 8, fountain: 6, garden: 8, nature_reserve: 8, beach: 8,
    waterfall: 9, forest: 7, picnic: 6, dog_park: 5, mosque: 7, synagogue: 7, cemetery: 5, exhibition: 6, film_location: 7,
  };
  return scores[place.kind] || 2;
}

function bearingDeg(
  from: { latitude: number; longitude: number },
  to: { latitude: number; longitude: number },
): number {
  const dLon = ((to.longitude - from.longitude) * Math.PI) / 180;
  const lat1 = (from.latitude * Math.PI) / 180;
  const lat2 = (to.latitude * Math.PI) / 180;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

function offsetAtBearing(
  origin: { latitude: number; longitude: number },
  meters: number,
  bearing: number,
): { latitude: number; longitude: number } {
  const R = 6371000;
  const br = (bearing * Math.PI) / 180;
  const lat1 = (origin.latitude * Math.PI) / 180;
  const lon1 = (origin.longitude * Math.PI) / 180;
  const ang = meters / R;
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(ang) + Math.cos(lat1) * Math.sin(ang) * Math.cos(br));
  const lon2 =
    lon1 +
    Math.atan2(
      Math.sin(br) * Math.sin(ang) * Math.cos(lat1),
      Math.cos(ang) - Math.sin(lat1) * Math.sin(lat2),
    );
  return { latitude: (lat2 * 180) / Math.PI, longitude: (lon2 * 180) / Math.PI };
}

function offsetPlace(
  origin: { latitude: number; longitude: number },
  meters: number,
  bearing = 25,
  name = "Paseo por el pueblo",
): NearbyPlace {
  const point = offsetAtBearing(origin, meters, bearing);
  return {
    id: `walk-${Math.round(bearing)}`,
    name,
    kind: "park",
    address: "Ruta calculada desde tu posición",
    latitude: point.latitude,
    longitude: point.longitude,
    distanceMeters: Math.round(meters),
  };
}

export type CircularRoute = {
  origin: { latitude: number; longitude: number };
  stops: NearbyPlace[];
  totalMinutes: number;
  stayLocal: boolean;
  names: string;
  dwellMinutes?: number[];
  modeLabel?: string;
};

function minutesBetween(
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
  mode: TravelMode,
): number {
  return Math.max(2, haversine(a, b) / SPEED_M_PER_MIN[mode]);
}

function angleDiff(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/** True if `point` stays on the same side of origin as the sector heading (±90°). */
function inSector(
  origin: { latitude: number; longitude: number },
  point: { latitude: number; longitude: number },
  sectorHeading: number,
): boolean {
  return angleDiff(bearingDeg(origin, point), sectorHeading) <= 95;
}

export function buildCircularRoute(input: {
  origin: { latitude: number; longitude: number };
  places: NearbyPlace[];
  mode: TravelMode;
  totalMinutes: number;
  stayLocal: boolean;
  social?: SocialContext;
  setting?: SettingPref;
  /** Interés mínimo de un sitio para ser parada (5 por defecto; con filtro de categorías se baja a 1). */
  minInterest?: number;
}): CircularRoute {
  const budget = Math.max(20, input.totalMinutes);
  const radius = input.stayLocal
    ? stayLocalRadiusMeters(input.mode)
    : Math.min(
        input.mode === "car" ? 25000 : input.mode === "bike" ? 8000 : 3500,
        Math.max(700, SPEED_M_PER_MIN[input.mode] * (budget / 2) * 1.05),
      );
  const filtered = input.social
    ? filterPlaces(input.places, {
        social: input.social,
        setting: input.setting ?? "any",
      })
    : input.places;
  const source = filtered.length ? filtered : input.places;
  const withDist = source
    .map((place) => ({
      ...place,
      distanceMeters: Math.round(place.distanceMeters ?? haversine(input.origin, place)),
    }))
    .filter((place) => place.name && place.distanceMeters <= radius * 1.05);

  const unique: NearbyPlace[] = [];
  for (const place of withDist.sort(
    (a, b) => interestScore(b) - interestScore(a) || a.distanceMeters! - b.distanceMeters!,
  )) {
    const near = unique.some((other) => haversine(place, other) < 90);
    if (!near) unique.push(place);
  }

  const named = unique.filter((place) => interestScore(place) >= (input.minInterest ?? 5));
  const pool = (named.length >= 2 ? named : unique).slice(0, 18);

  const first =
    pool.slice().sort((a, b) => {
      const da = Math.abs((a.distanceMeters || 0) - radius * 0.35);
      const db = Math.abs((b.distanceMeters || 0) - radius * 0.35);
      return interestScore(b) - interestScore(a) || da - db;
    })[0] || null;

  const sectorHeading = first ? bearingDeg(input.origin, first) : 25;
  const sectorPool = pool.filter((p) => inSector(input.origin, p, sectorHeading));
  const workPool = sectorPool.length >= 2 ? sectorPool : pool;

  const used = new Set<string>();
  const stops: NearbyPlace[] = [];
  let elapsed = 0;
  let cursor: { latitude: number; longitude: number } = input.origin;
  let heading = sectorHeading;

  const pickNext = () => {
    const open = workPool.filter((place) => !used.has(place.id) && inSector(input.origin, place, sectorHeading));
    if (!open.length) return null;
    open.sort((a, b) => {
      const ba = angleDiff(bearingDeg(cursor, a), heading);
      const bb = angleDiff(bearingDeg(cursor, b), heading);
      const da = Math.abs((a.distanceMeters || 0) - (stops.length ? radius * 0.45 : radius * 0.3));
      const db = Math.abs((b.distanceMeters || 0) - (stops.length ? radius * 0.45 : radius * 0.3));
      return ba - bb || interestScore(b) - interestScore(a) || da - db;
    });
    return open[0];
  };

  while (stops.length < 6) {
    const place = pickNext();
    if (!place) break;
    const hop = minutesBetween(cursor, place, input.mode);
    const home = minutesBetween(place, input.origin, input.mode);
    if (elapsed + hop + home > budget) {
      used.add(place.id);
      if (stops.length >= 1) break;
      continue;
    }
    if (haversine(cursor, place) < 80) {
      used.add(place.id);
      continue;
    }
    stops.push(place);
    used.add(place.id);
    elapsed += hop;
    cursor = place;
    heading = bearingDeg(input.origin, place);
  }

  // Pads only within sector and under budget.
  let pads = 0;
  const ring = input.stayLocal
    ? Math.min(700, radius * 0.45)
    : Math.min(radius * 0.4, SPEED_M_PER_MIN[input.mode] * (budget / 10));
  while (stops.length < 2 && pads < 2) {
    const padHeading = (sectorHeading + pads * 40) % 360;
    const extra = offsetPlace(input.origin, Math.max(220, ring), padHeading, `Paseo ${pads + 1}`);
    const hop = minutesBetween(cursor, extra, input.mode);
    const home = minutesBetween(extra, input.origin, input.mode);
    if (elapsed + hop + home > budget) break;
    stops.push(extra);
    elapsed += hop;
    cursor = extra;
    pads += 1;
  }

  elapsed += minutesBetween(cursor, input.origin, input.mode);
  if (!stops.length) {
    stops.push(offsetPlace(input.origin, Math.min(radius * 0.4, 600), sectorHeading, "Paseo por el pueblo"));
    elapsed = Math.min(budget, minutesBetween(input.origin, stops[0], input.mode) * 2);
  }

  return {
    origin: input.origin,
    stops: stops.slice(0, 8),
    totalMinutes: Math.min(budget, Math.round(elapsed)),
    stayLocal: input.stayLocal,
    names: stops.map((stop) => stop.name).join(" → "),
    modeLabel: "trayecto",
  };
}

/**
 * Tiempo libre: menos trayecto, más estancia. Empaqueta 1–3 paradas con dwell (IA o heurística).
 */
export async function buildLeisureRoute(input: {
  origin: { latitude: number; longitude: number };
  places: NearbyPlace[];
  mode: TravelMode;
  totalMinutes: number;
  stayLocal: boolean;
  social?: SocialContext;
  setting?: SettingPref;
  minInterest?: number;
  dwellFor: (place: NearbyPlace) => Promise<number>;
}): Promise<CircularRoute> {
  const budget = Math.max(30, input.totalMinutes);
  const maxTravelShare = Math.min(0.4, 0.55);
  const travelBudget = Math.max(15, Math.round(budget * maxTravelShare));
  const radius = input.stayLocal
    ? stayLocalRadiusMeters(input.mode)
    : Math.min(
        input.mode === "car" ? 18000 : input.mode === "bike" ? 7000 : 2800,
        SPEED_M_PER_MIN[input.mode] * (travelBudget / 2) * 1.1,
      );

  const filtered = input.social
    ? filterPlaces(input.places, {
        social: input.social,
        setting: input.setting ?? "any",
      })
    : input.places;
  const source = filtered.length ? filtered : input.places;
  const candidates = source
    .map((place) => ({
      ...place,
      distanceMeters: Math.round(place.distanceMeters ?? haversine(input.origin, place)),
    }))
    .filter((p) => p.name && interestScore(p) >= (input.minInterest ?? 5) && (p.distanceMeters || 0) <= radius)
    .sort((a, b) => interestScore(b) - interestScore(a) || (a.distanceMeters || 0) - (b.distanceMeters || 0))
    .slice(0, 10);

  const first = candidates[0];
  const sectorHeading = first ? bearingDeg(input.origin, first) : 30;
  const sector = candidates.filter((p) => inSector(input.origin, p, sectorHeading));
  const pool = (sector.length ? sector : candidates).slice(0, 8);

  const packed: NearbyPlace[] = [];
  const dwells: number[] = [];
  let travel = 0;
  let dwellSum = 0;
  let cursor = input.origin;

  for (const place of pool) {
    if (packed.length >= 3) break;
    const hop = minutesBetween(cursor, place, input.mode);
    const home = minutesBetween(place, input.origin, input.mode);
    const dwell = await input.dwellFor(place);
    if (travel + hop + home + dwellSum + dwell > budget) continue;
    if (travel + hop + home > travelBudget && packed.length >= 1) continue;
    packed.push(place);
    dwells.push(dwell);
    travel += hop;
    dwellSum += dwell;
    cursor = place;
  }

  if (!packed.length && pool[0]) {
    const place = pool[0];
    const hop = minutesBetween(input.origin, place, input.mode);
    const dwell = Math.min(
      budget - hop * 2,
      await input.dwellFor(place),
    );
    packed.push(place);
    dwells.push(Math.max(20, Math.floor(dwell)));
    travel = hop * 2;
    dwellSum = dwells[0];
  }

  if (!packed.length) {
    return buildCircularRoute({
      origin: input.origin,
      places: input.places,
      mode: input.mode,
      totalMinutes: budget,
      stayLocal: input.stayLocal,
      social: input.social,
      setting: input.setting,
      minInterest: input.minInterest,
    });
  }

  travel += minutesBetween(cursor, input.origin, input.mode);
  return {
    origin: input.origin,
    stops: packed,
    totalMinutes: Math.min(budget, Math.round(travel + dwellSum)),
    stayLocal: input.stayLocal,
    names: packed.map((s, i) => `${s.name} (~${dwells[i]} min)`).join(" → "),
    dwellMinutes: dwells,
    modeLabel: "libre",
  };
}

/** Pick a place near the target one-way distance whose round-trip fits the time budget. */
export async function pickTimedDestination(input: {
  origin: { latitude: number; longitude: number };
  places: NearbyPlace[];
  mode: TravelMode;
  totalMinutes: number;
  targetMeters: number;
  stayLocal?: boolean;
}): Promise<{ place: NearbyPlace; oneWayMinutes: number; route: CircularRoute } | null> {
  const route = buildCircularRoute({
    origin: input.origin,
    places: input.places,
    mode: input.mode,
    totalMinutes: input.totalMinutes,
    stayLocal: input.stayLocal ?? false,
  });
  const place = route.stops[Math.max(0, Math.floor((route.stops.length - 1) / 2))] || route.stops[0];
  if (!place) return null;
  const oneWay = Math.max(5, Math.round(input.totalMinutes / 2));
  return { place, oneWayMinutes: oneWay, route };
}
